import { useEffect, useRef, useState, useMemo } from "react";

interface MasonryLayoutProps<T> {
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  cardWidth?: number;
  cardGap?: number;
  maxColumns?: number;
  onGridWidthChange?: (width: number) => void;
}

export const MasonryLayout = <T,>({
  items,
  renderItem,
  cardWidth = 296,
  cardGap = 12,
  maxColumns = 3,
  onGridWidthChange,
}: MasonryLayoutProps<T>) => {
  const [gridCols, setGridCols] = useState(3);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateGridCols = () => {
      if (!containerRef.current || !cardRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const possibleCols = Math.floor(containerWidth / (cardWidth + cardGap));
      const newCols = Math.min(Math.max(possibleCols, 1), maxColumns);
      setGridCols(newCols);

      // Calculate actual grid width and notify parent
      const actualGridWidth = newCols * cardWidth + (newCols - 1) * cardGap;
      onGridWidthChange?.(actualGridWidth);
    };

    const resizeObserver = new ResizeObserver(updateGridCols);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [cardWidth, cardGap, maxColumns, onGridWidthChange]);

  // Memoize column organization to prevent creating new arrays on every render
  const columns = useMemo(() => {
    const cols = Array.from({ length: gridCols }, () => [] as T[]);
    items.forEach((item, i) => {
      cols[i % gridCols].push(item);
    });
    return cols;
  }, [items, gridCols]);

  return (
    <div className="w-full flex justify-start" ref={containerRef}>
      <div
        className="w-auto grid"
        style={{
          gridTemplateColumns: `repeat(${gridCols}, ${cardWidth}px)`,
          gap: `${cardGap}px`,
        }}
      >
        {columns.map((column, colIndex) => (
          <div
            key={colIndex}
            className="masonry-column relative flex flex-col"
            style={{ gap: `${cardGap}px` }}
          >
            {column.map((item, rowIndex) => (
              <div
                key={rowIndex}
                ref={colIndex === 0 && rowIndex === 0 ? cardRef : undefined}
                className="creative-card"
              >
                {renderItem(item)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
